import { tagged } from "@mirage/service-logging";
import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import * as scoring from "@mirage/service-typeahead-search/service/scoring";
import {
  recommendationMatchesWithEmptyQuery,
  recommendationMatchesWithQuery,
} from "@mirage/service-typeahead-search/service/search/utils";
import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import { getSourceLimit } from "@mirage/service-typeahead-search/service/utils/source-helpers";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import * as rx from "rxjs";
import { v5 as uuidv5 } from "uuid";
import { getResultSection } from "../utils/section-mapper";

const logger = tagged("typeahead/draft-from-source");
const DRAFT_NAMESPACE = "1b871a24-50d5-492e-99b0-da01ff1f5442";

const FILTER_RESULT_LIMIT = 200;

/**
 * API
 */

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { config, cache, grossLimit, sectionLimit },
) => {
  return rx.defer(() => {
    let results = cache.all(
      CacheKey.OmniboxRecommendations,
    ) as Recommendation<"omnibox">[];

    results = onlySummarizableDocuments(results);

    if (!query.length) {
      logger.debug(`Getting matches with empty query`);
      results = recommendationMatchesWithEmptyQuery<Recommendation<"omnibox">>(
        results,
        FILTER_RESULT_LIMIT,
      );
    } else {
      results = recommendationMatchesWithQuery<Recommendation<"omnibox">>(
        query,
        results,
        FILTER_RESULT_LIMIT,
      );
    }

    return rx.from(
      tagAndInjectHits(
        results,
        mode,
        query,
        config,
        cache,
        grossLimit,
        sectionLimit,
      ),
    );
  });
};

/**
 * Helpers
 */
export function onlySummarizableDocuments(
  candidates: Recommendation<"omnibox">[],
): Recommendation<"omnibox">[] {
  return candidates.filter(
    // SummarizableTypeYesSummarizable
    (candidate) =>
      candidate.summarizable === "yes_summarizable" &&
      candidate.recordType[".tag"] === "document",
  );
}

// NOTE (AYC): improvement we should move this into a utility file since
// it is also used by recommendation sources
export function tagAndInjectHits(
  results: Recommendation<"omnibox">[],
  mode: typeahead.Mode,
  query: string,
  config: typeahead.Config,
  cache: TypeaheadCache,
  grossLimit: number,
  sectionLimit: number | undefined,
): typeahead.ScoredResult[] {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.DraftWithSource,
    query,
    grossLimit,
    sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.DraftWithSource,
    mode,
    query,
    config,
  );

  return results
    .map((result) => {
      // we can have click tracking from the server through recommendations,
      // and we can have click tracking locally. The server can be delayed,
      // so in order to provide the best user experience, we use whichever
      // source has the highest non-title-match score for the recommendation
      // that way when the server catches up, it'll just start transparently
      // using the server-side score over the client-side score
      const draftWithSourceUuid = uuidv5(result.uuid, DRAFT_NAMESPACE);
      const resultWithLocalHits = primitives.draftWithSource(
        // result.uuid,
        draftWithSourceUuid,
        result,
        {
          hits: cache.getHits(result.uuid),
          sectionId,
        },
      );
      const resultWithRemoteHits = primitives.draftWithSource(
        // result.uuid,
        draftWithSourceUuid,
        result,
        {
          hits: {
            history: result.clicks,
            mostRecentMs: result.lastClickedMs,
          },
          sectionId,
        },
      );

      const localScoredResult = scoring.sourceScore(
        query,
        config,
      )(resultWithLocalHits);
      const remoteScoredResult = scoring.sourceScore(
        query,
        config,
      )(resultWithRemoteHits);

      return localScoredResult.score > remoteScoredResult.score
        ? localScoredResult
        : remoteScoredResult;
    })
    .sort((a, b) => b.score! - a.score!)
    .slice(0, sourceLimit);
}

export const search = wrappers.wrapped(SourceId.DraftWithSource, raw);
